import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import UpdateOrganizationRequest, {
    UpdateOrganizationRequestFieldName,
} from '@shared/models/organizations/UpdateOrganizationRequest';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useStateRef from './useStateRef';

export function useCurrentOrgDetailsEditor(initialModel: OrganizationModel | null) {
    const { t } = useTranslation();
    const { updateOrganization } = OrganizationContainer.useContainer();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const [editOrganization, setEditOrganization, editOrganizationRef] = useStateRef<UpdateOrganizationRequest | null>(
        initialModel as UpdateOrganizationRequest
    );
    const [hasChanges, setHasChanges] = useState(false);

    const resetEditOrganization = () => {
        setEditOrganization(initialModel as UpdateOrganizationRequest);
        setHasChanges(false);
    };

    const handleFieldChange = <T extends UpdateOrganizationRequestFieldName>(
        fieldName: T,
        value: UpdateOrganizationRequest[T]
    ) => {
        setEditOrganization((prevModel) =>
            prevModel
                ? {
                      ...prevModel,
                      [fieldName]: value,
                  }
                : null
        );
        if (!hasChanges) setHasChanges(true);
    };

    const handleInputChange = <T extends UpdateOrganizationRequestFieldName>(
        fieldName: T,
        value: UpdateOrganizationRequest[T]
    ) => {
        handleFieldChange(fieldName, value);
    };

    const handleUpdate = async () => {
        try {
            if (!initialModel?.id || !editOrganizationRef.current) throw new Error('Missing data');
            const result = await updateOrganization(initialModel.id, editOrganizationRef.current);
            if (result.resultType === 'Ok' && result.data) {
                setEditOrganization(result.data as UpdateOrganizationRequest);
                setHasChanges(false);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<OrganizationModel>();
        }
    };

    const handleAlerts = (result: IResult<OrganizationModel>) => {
        if (result.resultType === 'Ok') {
            showSuccess(t('common:saved'));
        } else {
            showError(t('common:saveError'));
        }
    };

    const handleUpdateWithAlerts = async () => {
        const result = await handleUpdate();
        handleAlerts(result);
        return result;
    };

    return {
        hasChanges,
        editOrganization,
        setEditOrganization,
        handleFieldChange,
        handleInputChange,
        handleUpdate,
        resetEditOrganization,
        handleUpdateWithAlerts,
    };
}
